import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
    handleLicenseError, 
    LICENSE_ERROR_CODES, 
    parseLicenseError,
    getSeverityStyle 
} from '../utils/licenseErrorHandler';
import QuotaExceededModal from '../components/shared/QuotaExceededModal';
import FeatureUpgradeModal from '../components/shared/FeatureUpgradeModal';

const LicenseErrorContext = createContext();

export const useLicenseError = () => {
    const context = useContext(LicenseErrorContext);
    if (!context) {
        throw new Error('useLicenseError must be used within a LicenseErrorProvider');
    }
    return context;
};

export const LicenseErrorProvider = ({ children }) => {
    const [quotaModal, setQuotaModal] = useState({ isOpen: false, error: null });
    const [featureModal, setFeatureModal] = useState({ isOpen: false, error: null });
    const [currentError, setCurrentError] = useState(null);

    // Show quota exceeded modal
    const showQuotaExceededModal = useCallback((licenseError) => {
        setQuotaModal({ isOpen: true, error: licenseError });
        setCurrentError(licenseError);
    }, []);

    // Show feature upgrade modal
    const showFeatureUpgradeModal = useCallback((licenseError) => {
        setFeatureModal({ isOpen: true, error: licenseError });
        setCurrentError(licenseError);
    }, []);

    // Close quota modal
    const closeQuotaModal = useCallback(() => {
        setQuotaModal({ isOpen: false, error: null });
        setCurrentError(null);
    }, []);

    // Close feature modal
    const closeFeatureModal = useCallback(() => {
        setFeatureModal({ isOpen: false, error: null });
        setCurrentError(null);
    }, []);

    // Show license error toast
    const showLicenseErrorToast = useCallback((licenseError) => {
        const severityStyle = getSeverityStyle(licenseError.severity || 'error');
        
        const toastOptions = {
            position: "top-right",
            autoClose: licenseError.severity === 'error' ? false : 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
        };

        const errorMessage = (
            <div>
                <div className="font-medium">{licenseError.title || licenseError.error}</div>
                {licenseError.help && (
                    <div className="text-sm mt-1 opacity-90">{licenseError.help}</div>
                )}
                {licenseError.actionUrl && (
                    <button
                        onClick={() => window.open(licenseError.actionUrl, '_blank')}
                        className="text-sm underline mt-2 block hover:no-underline"
                    >
                        {licenseError.action || 'Take Action'}
                    </button>
                )}
            </div>
        );

        switch (licenseError.severity) {
            case 'error':
                toast.error(errorMessage, toastOptions);
                break;
            case 'warning':
                toast.warn(errorMessage, toastOptions);
                break;
            case 'info':
            default:
                toast.info(errorMessage, toastOptions);
                break;
        }
    }, []);

    // Main error handler function
    const handleError = useCallback((error, options = {}) => {
        const licenseError = parseLicenseError(error);
        
        if (!licenseError) {
            // Not a license error, handle as regular error
            if (options.showToast !== false) {
                toast.error(error.message || 'An error occurred');
            }
            return false;
        }

        const {
            suppressModal = false,
            suppressToast = false,
            customHandler
        } = options;

        // Use custom handler if provided
        if (customHandler) {
            return customHandler(licenseError);
        }

        // Handle specific license error types
        const wasHandled = handleLicenseError(licenseError, {
            onQuotaExceeded: (error) => {
                if (!suppressModal) {
                    showQuotaExceededModal(error);
                } else if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
            },
            onFeatureNotAvailable: (error) => {
                if (!suppressModal) {
                    showFeatureUpgradeModal(error);
                } else if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
            },
            onLicenseExpired: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
                // Redirect to license renewal
                if (error.actionUrl) {
                    setTimeout(() => {
                        window.location.href = error.actionUrl;
                    }, 2000);
                }
            },
            onLicenseInvalid: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
                // Redirect to license setup
                if (error.actionUrl) {
                    setTimeout(() => {
                        window.location.href = error.actionUrl;
                    }, 2000);
                }
            },
            onLicenseSuspended: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
            },
            onSetupRequired: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
                // Redirect to setup
                if (error.actionUrl) {
                    setTimeout(() => {
                        window.location.href = error.actionUrl;
                    }, 2000);
                }
            },
            onAuthRequired: (error) => {
                // Clear token and redirect to login
                localStorage.removeItem('token');
                window.location.href = error.actionUrl || '/login';
            },
            onInsufficientPermissions: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
            },
            onRateLimitExceeded: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
            },
            onMaintenanceMode: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
            },
            onGenericError: (error) => {
                if (!suppressToast) {
                    showLicenseErrorToast(error);
                }
            },
            showToast: !suppressToast,
            redirectOnAuth: true
        });

        return wasHandled;
    }, [showQuotaExceededModal, showFeatureUpgradeModal, showLicenseErrorToast]);

    // Check if a specific quota type is exceeded
    const isQuotaExceeded = useCallback((quotaType) => {
        if (!currentError || !currentError._quotas) return false;
        const quota = currentError._quotas[quotaType];
        return quota && quota.isExceeded;
    }, [currentError]);

    // Check if a specific feature is available
    const isFeatureAvailable = useCallback((featureName) => {
        if (!currentError || !currentError._license || !currentError._license.features) {
            return true; // Assume available if no license error context
        }
        return currentError._license.features[featureName] !== false;
    }, [currentError]);

    // Get current blocking level
    const getBlockingLevel = useCallback(() => {
        if (!currentError || !currentError._license) return 0;
        return currentError._license.blockingLevel || 0;
    }, [currentError]);

    // Get quota information for a specific type
    const getQuotaInfo = useCallback((quotaType) => {
        if (!currentError || !currentError._quotas) return null;
        return currentError._quotas[quotaType] || null;
    }, [currentError]);

    const value = {
        // State
        currentError,
        quotaModal,
        featureModal,
        
        // Actions
        handleError,
        showQuotaExceededModal,
        showFeatureUpgradeModal,
        closeQuotaModal,
        closeFeatureModal,
        showLicenseErrorToast,
        
        // Utilities
        isQuotaExceeded,
        isFeatureAvailable,
        getBlockingLevel,
        getQuotaInfo,
        
        // Clear current error context
        clearError: () => setCurrentError(null)
    };

    return (
        <LicenseErrorContext.Provider value={value}>
            {children}
            
            {/* Global License Error Modals */}
            <QuotaExceededModal
                isOpen={quotaModal.isOpen}
                onClose={closeQuotaModal}
                licenseError={quotaModal.error}
            />
            
            <FeatureUpgradeModal
                isOpen={featureModal.isOpen}
                onClose={closeFeatureModal}
                licenseError={featureModal.error}
            />
        </LicenseErrorContext.Provider>
    );
};

export default LicenseErrorProvider;